import React, { useState, useEffect } from "react";

/**
 * DeviceGuard — monitors viewport dimensions and orientation.
 * - Under 768px: Mobile phone detected -> Displays cyberpunk station incompatible notice.
 * - 768px to 1199px in portrait: Tablet in portrait detected -> Displays animated rotate to landscape guide.
 * - Otherwise: Valid station (laptop, desktop, or landscape tablet).
 */
export default function DeviceGuard({ children }) {
  const [windowState, setWindowState] = useState(() => {
    if (typeof window === "undefined") {
      return { width: 1440, height: 900, isPhone: false, isTabletPortrait: false };
    }
    const w = window.innerWidth;
    const h = window.innerHeight;
    return {
      width: w,
      height: h,
      isPhone: w < 768,
      isTabletPortrait: w >= 768 && w < 1200 && h > w,
    };
  });

  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      setWindowState({
        width: w,
        height: h,
        isPhone: w < 768,
        isTabletPortrait: w >= 768 && w < 1200 && h > w,
      });
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  // 1. Mobile Phone Guard: Stationary device required
  if (windowState.isPhone) {
    return (
      <div
        data-testid="device-guard-phone"
        className="grain"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          background: "var(--ink, #0a0a0f)",
          color: "var(--paper, #f5f5f2)",
          fontFamily: "var(--font-mono)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          padding: "calc(24px + var(--sat, 0px)) calc(24px + var(--sar, 0px)) calc(24px + var(--sab, 0px)) calc(24px + var(--sal, 0px))",
          overflowY: "auto",
        }}
      >
        {/* Top Header */}
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <span style={{ width: 8, height: 8, background: "var(--neon-magenta)", display: "inline-block", boxShadow: "0 0 10px var(--neon-magenta)" }} />
            <span style={{ fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--neon-magenta)" }}>
              STATION INCOMPATIBLE // HARDWARE REQ
            </span>
          </div>
          <div style={{ height: 1, width: "100%", background: "rgba(255,46,136,0.3)" }} />
        </div>

        {/* Center Content */}
        <div style={{ margin: "32px 0", maxWidth: 480 }}>
          <h1
            className="font-serif"
            style={{
              fontSize: "clamp(32px, 8.5vw, 44px)",
              lineHeight: 1.05,
              margin: 0,
              letterSpacing: "-0.02em",
              color: "var(--paper)",
            }}
          >
            open on a <em style={{ color: "var(--neon-cyan)", fontStyle: "italic" }}>laptop</em> or <span className="neon-magenta">desktop.</span>
          </h1>

          <p style={{ marginTop: 18, fontSize: 13, lineHeight: 1.6, opacity: 0.8, color: "var(--paper)" }}>
            Dard-e-Disco uses dual-model computer vision to track your head gaze and movements in real-time.
          </p>

          <p style={{ marginTop: 12, fontSize: 12, lineHeight: 1.6, opacity: 0.7 }}>
            A stable stationary display (laptop, desktop, or tablet on a desk/stand) is required so the camera can see your movements. Holding a phone in hand restricts your freedom of movement.
          </p>

          {/* Specs Checklist */}
          <div
            style={{
              marginTop: 24,
              padding: "14px 16px",
              background: "rgba(255, 255, 255, 0.04)",
              border: "1px solid rgba(255, 255, 255, 0.12)",
              borderRadius: 8,
              fontSize: 11,
              letterSpacing: "0.08em",
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ color: "var(--neon-cyan)" }}>◆</span>
              <span>CAMERA TRACKING // Stationary camera to track movements</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ color: "var(--neon-magenta)" }}>◆</span>
              <span>SPATIAL AUDIO // 3D binaural soundscape (headphones recommended)</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ color: "var(--neon-lime)" }}>◆</span>
              <span>HOLOGRAM DISPLAY // Procedural stick-figure dance screen</span>
            </div>
          </div>
        </div>

        {/* Bottom Telemetry Footer */}
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 16, fontSize: 10, opacity: 0.6, letterSpacing: "0.14em", textTransform: "uppercase" }}>
          <div>detected viewport: {windowState.width}px × {windowState.height}px</div>
          <div style={{ marginTop: 4, color: "var(--neon-cyan)" }}>minimum screen width: 768px (landscape)</div>
        </div>
      </div>
    );
  }

  // 2. Tablet Portrait Guard: Prompt to rotate to landscape on desk/stand
  if (windowState.isTabletPortrait) {
    return (
      <div
        data-testid="device-guard-rotate"
        className="grain"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          background: "var(--ink, #0a0a0f)",
          color: "var(--paper, #f5f5f2)",
          fontFamily: "var(--font-mono)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "32px",
          textAlign: "center",
        }}
      >
        {/* Animated Rotate Icon */}
        <div style={{ position: "relative", width: 90, height: 90, marginBottom: 28 }}>
          <svg width="90" height="90" viewBox="0 0 90 90" fill="none" style={{ animation: "pulse-ring 2.5s infinite" }}>
            {/* Tablet outline portrait transitioning to landscape */}
            <rect
              x="22"
              y="15"
              width="46"
              height="60"
              rx="6"
              stroke="var(--neon-cyan)"
              strokeWidth="2.5"
              strokeDasharray="4 3"
            />
            {/* Tablet outline landscape */}
            <rect
              x="15"
              y="22"
              width="60"
              height="46"
              rx="6"
              stroke="var(--neon-magenta)"
              strokeWidth="2.5"
            />
            {/* Camera dot on landscape edge */}
            <circle cx="45" cy="26" r="2.5" fill="var(--neon-cyan)" />
          </svg>
        </div>

        <div style={{ fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--neon-magenta)", marginBottom: 8 }}>
          ORIENTATION REQUIRED
        </div>

        <h1
          className="font-serif"
          style={{
            fontSize: "clamp(30px, 5.5vw, 44px)",
            lineHeight: 1.1,
            margin: 0,
            maxWidth: 500,
            letterSpacing: "-0.01em",
          }}
        >
          rotate device to <em style={{ color: "var(--neon-cyan)" }}>landscape</em>
        </h1>

        <p style={{ marginTop: 16, maxWidth: 440, fontSize: 13, lineHeight: 1.6, opacity: 0.8 }}>
          Set your tablet on a desk or stand in landscape mode so the camera can see your movements.
        </p>

        <div style={{ marginTop: 24, padding: "8px 16px", borderRadius: 999, border: "1px solid rgba(0,211,217,0.4)", background: "rgba(0,211,217,0.08)", fontSize: 11, letterSpacing: "0.12em", color: "var(--neon-cyan)" }}>
          auto-detecting orientation: {windowState.width}px × {windowState.height}px (portrait)
        </div>
      </div>
    );
  }

  // 3. Fully compatible station (Laptop, Desktop, or Landscape Tablet)
  return children;
}
